import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Breadcrumb,
  BrandLockup,
  Button,
  Cluster,
  CommandPalette,
  HomeIcon,
  SearchIcon,
  PlusIcon,
  SwitchIcon,
  MenuIcon,
  CloseIcon,
  DashboardIcon,
  ReviewIcon,
  CampaignIcon,
  AssetIcon,
  CreativeIcon,
  ConnectorIcon,
  BellIcon,
  cx,
  type BreadcrumbItem,
  type CommandItem,
  type IconProps,
} from '@integra/ui';
import {
  useWorkspaces,
  useAssets,
  useCampaigns,
  useConnectorConfigs,
  useCollectAnalytics,
  useLogout,
} from '../api/hooks.js';
import { ErrorBoundary } from './ErrorBoundary.js';
import { useBrandTheme } from './useBrandTheme.js';

/**
 * Application chrome (§17.2 Sidebar + Breadcrumb, §3.3 branding). Navy chrome,
 * gold accents, cream content — the Fruitful Bough brand system through tokens.
 *
 * Navigation signals: the estate mark returns you to the workspace list, a Home
 * control returns you to the workspace dashboard, an icon'd sidebar makes each
 * section scannable, and a breadcrumb answers "where am I" on every screen.
 */

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly Icon: ComponentType<IconProps>;
  /** Which stage of the distribution loop this item belongs to. */
  readonly group: NavGroup;
}

/**
 * The navigation is shaped like the product's one job: publish once, land right on
 * every channel. The rail groups follow the actual pipeline order — Plan → Create →
 * Distribute → Measure → Optimise — so the menu itself explains what INTEGRA does.
 * `Govern` is the pinned reference/guardrail footer, not a loop stage.
 */
type NavGroup = 'Plan' | 'Create' | 'Distribute' | 'Measure' | 'Optimise' | 'Govern';

const NAV_GROUPS: readonly NavGroup[] = ['Plan', 'Create', 'Distribute', 'Measure', 'Optimise'];

/** Loop-stage numerals that reinforce the pipeline order in the rail. */
const GROUP_STEP: Partial<Record<NavGroup, string>> = {
  Plan: '1',
  Create: '2',
  Distribute: '3',
  Measure: '4',
  Optimise: '5',
};

/** Home is the command centre; it sits above the loop groups, not inside one. */
const HOME_ITEM: NavItem = {
  to: 'dashboard',
  label: 'Home',
  Icon: HomeIcon,
  group: 'Plan',
};

const NAV: readonly NavItem[] = [
  { to: 'campaigns', label: 'Campaigns', Icon: CampaignIcon, group: 'Plan' },
  { to: 'ai-assistant', label: 'AI assistant', Icon: CreativeIcon, group: 'Plan' },
  { to: 'assets', label: 'Assets', Icon: AssetIcon, group: 'Create' },
  { to: 'import', label: 'Import', Icon: PlusIcon, group: 'Create' },
  { to: 'creative', label: 'Creative studio', Icon: CreativeIcon, group: 'Create' },
  { to: 'localization', label: 'Localization', Icon: AssetIcon, group: 'Create' },
  { to: 'connectors', label: 'Integrations', Icon: ConnectorIcon, group: 'Distribute' },
  { to: 'engage', label: 'Engage', Icon: BellIcon, group: 'Distribute' },
  { to: 'preview', label: 'Preview', Icon: CreativeIcon, group: 'Distribute' },
  { to: 'webhooks', label: 'API & webhooks', Icon: ConnectorIcon, group: 'Distribute' },
  { to: 'performance', label: 'Performance', Icon: DashboardIcon, group: 'Measure' },
  { to: 'attribution', label: 'Attribution', Icon: DashboardIcon, group: 'Measure' },
  { to: 'weekly-review', label: 'Weekly review', Icon: ReviewIcon, group: 'Measure' },
  { to: 'reports', label: 'Client reports', Icon: ReviewIcon, group: 'Measure' },
  { to: 'experiments', label: 'Experiments', Icon: ReviewIcon, group: 'Measure' },
  { to: 'optimise', label: 'Optimise', Icon: ReviewIcon, group: 'Optimise' },
];

/** Pinned below the loop: the guardrails and the reference library. */
const GOVERN_NAV: readonly NavItem[] = [
  { to: 'brand-safety', label: 'Brand safety', Icon: ConnectorIcon, group: 'Govern' },
  { to: 'channel-standards', label: 'Channel standards', Icon: AssetIcon, group: 'Govern' },
  { to: 'billing', label: 'Billing', Icon: ReviewIcon, group: 'Govern' },
];

/**
 * The shell renders OUTSIDE <Routes>, so `useParams()` sees no route match and
 * would always return undefined. The active workspace is therefore read from
 * the pathname — the shell needs it to build navigation that the routes below
 * it will match.
 */
function useWorkspaceIdFromPath(): { workspaceId: string | undefined; pathname: string } {
  const { pathname } = useLocation();
  const match = /^\/w\/([^/]+)/.exec(pathname);
  return { workspaceId: match?.[1], pathname };
}

function titleCase(segment: string): string {
  return segment
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** One rail row — shared by Home, the loop items, and the Govern footer. */
function NavRow({ workspaceId, item }: { workspaceId: string; item: NavItem }) {
  return (
    <li>
      <NavLink
        to={`/w/${workspaceId}/${item.to}`}
        className={({ isActive }) =>
          cx(
            'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors duration-fast',
            isActive
              ? 'bg-navy-600 font-semibold text-ink-inverse'
              : 'text-navy-300 hover:bg-navy-600 hover:text-ink-inverse',
          )
        }
      >
        {({ isActive }: { isActive: boolean }) => (
          <>
            <item.Icon size={18} className={isActive ? 'text-gold-400' : undefined} />
            {item.label}
          </>
        )}
      </NavLink>
    </li>
  );
}

/**
 * An organisation-level rail row pointing at an absolute route (e.g. branding),
 * rather than a workspace-scoped loop item. Styled to match {@link NavRow} so a
 * cross-workspace setting reads as a first-class destination, not an aside.
 */
function OrgNavRow({ to, label, Icon }: { to: string; label: string; Icon: NavItem['Icon'] }) {
  return (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) =>
          cx(
            'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors duration-fast',
            isActive
              ? 'bg-navy-600 font-semibold text-ink-inverse'
              : 'text-navy-300 hover:bg-navy-600 hover:text-ink-inverse',
          )
        }
      >
        {({ isActive }: { isActive: boolean }) => (
          <>
            <Icon size={18} className={isActive ? 'text-gold-400' : undefined} />
            {label}
          </>
        )}
      </NavLink>
    </li>
  );
}

/** Organisation-level destinations, surfaced below the workspace loop. */
const ORG_NAV = [
  { to: '/settings/branding', label: 'Customise & branding', Icon: CreativeIcon },
  { to: '/settings/security', label: 'Security & SSO', Icon: ConnectorIcon },
] as const;

/** The pinned "Organisation" section — a first-class home for cross-workspace
 *  settings like white-label branding (§20.5), reachable from any state. */
function OrgSection() {
  return (
    <div className="mt-1 border-t border-navy-600 pt-3">
      <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-navy-400">
        Organisation
      </p>
      <ul className="flex flex-col gap-1">
        {ORG_NAV.map((item) => (
          <OrgNavRow key={item.to} to={item.to} label={item.label} Icon={item.Icon} />
        ))}
      </ul>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { workspaceId, pathname } = useWorkspaceIdFromPath();
  const { data: workspaces } = useWorkspaces();
  const logout = useLogout();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  // The rail is a persistent column on desktop and an off-canvas drawer on
  // narrow screens (§17.2). Tracked here so the hamburger, backdrop, and drawer
  // stay in sync; the drawer auto-closes on navigation (effect below).
  const [navOpen, setNavOpen] = useState(false);

  const activeWorkspace = workspaces?.data.find((w) => w.id === workspaceId);

  // Org-level settings routes (/settings/*) carry no workspace in the path, but
  // the user still needs the full rail to get back into their work. Fall back to
  // the first workspace for nav targets so the pipeline doesn't vanish there. The
  // /workspaces picker keeps the minimal rail (navWorkspaceId stays undefined).
  const navWorkspaceId =
    workspaceId ?? (pathname.startsWith('/settings') ? workspaces?.data[0]?.id : undefined);
  const navWorkspace = workspaces?.data.find((w) => w.id === navWorkspaceId);

  // Per-organisation white-label branding (§20.5): resolves the product name,
  // tagline, and logo for the lockup, and injects the org's brand colours onto
  // the document root so the chrome recolours. Estate brand when unset.
  const brand = useBrandTheme();

  // Workspace-scoped data powers both the command bar's entity search and the
  // persistent setup nudge. Disabled (no fetch) until a workspace is in the path.
  const wsId = workspaceId ?? '';
  const assets = useAssets(wsId);
  const campaigns = useCampaigns(wsId);
  const configs = useConnectorConfigs(wsId);
  const collect = useCollectAnalytics(wsId);

  // A tiny transient toast so an inline action gives visible feedback — the app
  // has no toast system, and an action that runs silently reads as broken.
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const showToast = useCallback((message: string): void => {
    setToast(message);
    if (toastTimer.current !== null) globalThis.clearTimeout(toastTimer.current);
    // A UI dismissal timer, not backend job scheduling (§21.2) — hence globalThis.
    toastTimer.current = globalThis.setTimeout(() => setToast(null), 3500);
  }, []);

  // Setup progress on real counts — the same three steps as Home, surfaced here
  // so the nudge follows the user until the loop is proven once.
  const connectorCount = configs.data?.data.length ?? 0;
  const assetCount = assets.data?.data.length ?? 0;
  const publishedOnce = (campaigns.data?.data ?? []).some(
    (c) => c.state === 'running' || c.state === 'completed',
  );
  const setupSteps = [
    { label: 'Connect an integration', done: connectorCount > 0, to: 'connectors' },
    { label: 'Import an asset', done: assetCount > 0, to: 'assets' },
    { label: 'Publish once', done: publishedOnce, to: 'assets' },
  ];
  const setupDone = setupSteps.filter((s) => s.done).length;
  const setupComplete = setupDone === setupSteps.length;
  const nextStep = setupSteps.find((s) => !s.done);

  // Sign out through the hooks layer (§21.4): the mutation revokes the httpOnly
  // cookie server-side, then clears the in-memory token. No raw fetch in a screen.
  const signOut = (): void => logout.mutate();

  // ⌘K / Ctrl-K opens the command palette from anywhere (§17.2); Escape closes
  // the mobile navigation drawer.
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
      if (event.key === 'Escape') setNavOpen(false);
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, []);

  // A route change always dismisses the drawer — tapping a nav item on mobile
  // should reveal the destination, not leave the overlay covering it.
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  const commands = useMemo<CommandItem[]>(() => {
    const items: CommandItem[] = [];
    if (workspaceId !== undefined) {
      // Actions first — the command bar should DO, not only navigate.
      items.push({
        id: 'act-publish',
        label: 'Publish an asset',
        group: 'Actions',
        hint: 'review, then publish',
        keywords: 'broadcast post share',
        run: () => navigate(`/w/${workspaceId}/campaigns?launch=review-publish`),
      });
      items.push({
        id: 'act-new-campaign',
        label: 'New campaign',
        group: 'Actions',
        hint: 'visual builder',
        keywords: 'plan sequence',
        run: () => navigate(`/w/${workspaceId}/campaigns/new`),
      });
      items.push({
        id: 'act-add-channel',
        label: 'Add a channel',
        group: 'Actions',
        keywords: 'connector connect destination',
        run: () => navigate(`/w/${workspaceId}/connectors`),
      });
      // A real inline action — runs a mutation and reports back, right here.
      items.push({
        id: 'act-collect',
        label: 'Collect analytics now',
        group: 'Actions',
        hint: 'pull fresh metrics',
        keywords: 'refresh gather metrics update',
        run: () => {
          showToast('Collecting analytics…');
          collect.mutate(undefined, {
            onSuccess: (r) =>
              showToast(
                `Recorded ${r.pointsRecorded.toLocaleString()} data point${r.pointsRecorded === 1 ? '' : 's'}`,
              ),
            onError: () => showToast("Couldn't collect analytics — try again"),
          });
        },
      });
      for (const nav of [HOME_ITEM, ...NAV, ...GOVERN_NAV]) {
        items.push({
          id: `nav-${nav.to}`,
          label: nav.label,
          group: 'Go to',
          run: () => navigate(`/w/${workspaceId}/${nav.to}`),
        });
      }
      // Find anything: real entities by name, so ⌘K jumps straight to them.
      for (const asset of assets.data?.data ?? []) {
        const title =
          ((asset.data as Record<string, unknown>)['title'] as string | undefined) ?? asset.id;
        items.push({
          id: `asset-${asset.id}`,
          label: title,
          group: 'Assets',
          keywords: 'asset content product',
          run: () => navigate(`/w/${workspaceId}/assets/${asset.id}`),
        });
      }
      for (const campaign of campaigns.data?.data ?? []) {
        items.push({
          id: `campaign-${campaign.id}`,
          label: campaign.name,
          group: 'Campaigns',
          keywords: 'campaign launch',
          run: () => navigate(`/w/${workspaceId}/campaigns/${campaign.id}`),
        });
      }
      for (const config of configs.data?.data ?? []) {
        items.push({
          id: `channel-${config.id}`,
          label: config.displayName,
          group: 'Integrations',
          keywords: 'integration channel connector destination source',
          run: () => navigate(`/w/${workspaceId}/connectors`),
        });
      }
    }
    for (const workspace of workspaces?.data ?? []) {
      items.push({
        id: `ws-${workspace.id}`,
        label: workspace.name,
        group: 'Workspace',
        keywords: 'switch',
        run: () => navigate(`/w/${workspace.id}/dashboard`),
      });
    }
    items.push({
      id: 'all-workspaces',
      label: 'All workspaces',
      group: 'Go to',
      run: () => navigate('/workspaces'),
    });
    items.push({ id: 'sign-out', label: 'Sign out', group: 'Account', run: () => void signOut() });
    return items;
  }, [
    workspaceId,
    workspaces,
    navigate,
    assets.data,
    campaigns.data,
    configs.data,
    collect,
    showToast,
  ]);

  // "You are here" trail: Workspaces / <workspace> / <section> / <subpage>.
  const crumbs = useMemo<BreadcrumbItem[]>(() => {
    const parts = pathname.split('/').filter(Boolean); // ['w', wsId, section, sub?]
    const items: BreadcrumbItem[] = [
      { label: 'Workspaces', onClick: () => navigate('/workspaces') },
    ];
    if (workspaceId !== undefined && activeWorkspace !== undefined) {
      items.push({
        label: activeWorkspace.name,
        onClick: () => navigate(`/w/${workspaceId}/dashboard`),
      });
      const section = parts[2];
      const sub = parts[3];
      if (section !== undefined) {
        const label =
          [HOME_ITEM, ...NAV, ...GOVERN_NAV].find((n) => n.to === section)?.label ??
          titleCase(section);
        items.push({
          label,
          ...(sub !== undefined ? { onClick: () => navigate(`/w/${workspaceId}/${section}`) } : {}),
        });
        if (sub !== undefined) items.push({ label: sub === 'new' ? 'New' : 'Detail' });
      }
    }
    return items;
  }, [pathname, workspaceId, activeWorkspace, navigate]);

  return (
    <div className="reading-layer flex min-h-screen bg-surface-muted">
      {/* Drawer backdrop — only on narrow screens, only when the rail is open.
          Tapping it (or Escape) dismisses the navigation. */}
      {navOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-40 bg-navy-900/60 backdrop-blur-sm lg:hidden"
        />
      )}

      <aside
        className={cx(
          // Off-canvas drawer on mobile: fixed, elevated, and slid out of view
          // until opened. A persistent in-flow rail from `lg` up.
          'fixed inset-y-0 left-0 z-50 flex h-screen w-[17rem] max-w-[85vw] flex-col bg-navy-700 text-ink-inverse shadow-raised transition-transform duration-base',
          'lg:sticky lg:top-0 lg:z-auto lg:w-60 lg:max-w-none lg:shrink-0 lg:shadow-none lg:transition-none',
          navOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className="flex items-center justify-between border-b border-navy-600 pr-2">
          <Link
            to="/workspaces"
            className="block flex-1 px-5 py-4 transition-colors duration-fast hover:bg-navy-600"
            aria-label={`${brand.productName} home, all workspaces`}
          >
            <BrandLockup
              tone="light"
              productName={brand.productName}
              tagline={brand.tagline}
              logoUrl={brand.logoUrl}
            />
          </Link>
          {/* Close affordance inside the drawer, for reach on mobile. */}
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label="Close navigation"
            className="flex h-9 w-9 items-center justify-center rounded-md text-navy-300 transition-colors duration-fast hover:bg-navy-600 hover:text-ink-inverse lg:hidden"
          >
            <CloseIcon size={20} />
          </button>
        </div>

        <nav className="flex flex-1 flex-col overflow-y-auto px-3 py-4">
          {navWorkspaceId === undefined ? (
            <>
              <NavLink
                to="/workspaces"
                className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-navy-300 hover:bg-navy-600 hover:text-ink-inverse"
              >
                <HomeIcon size={18} />
                Workspaces
              </NavLink>
              <div className="mt-auto">
                <OrgSection />
              </div>
            </>
          ) : (
            <>
              <ul className="flex flex-col gap-1">
                <li>
                  <NavLink
                    to="/workspaces"
                    className="mb-1 flex items-center gap-2 rounded-md px-3 py-1.5 text-xs uppercase tracking-wide text-navy-300 hover:bg-navy-600"
                  >
                    ← All workspaces
                  </NavLink>
                </li>
                <NavRow workspaceId={navWorkspaceId} item={HOME_ITEM} />
              </ul>

              {/* The loop: Plan → Create → Distribute → Measure → Optimise. The
                  group numerals make the pipeline order legible at a glance. */}
              <ul className="flex flex-col gap-1">
                {NAV_GROUPS.map((group) => (
                  <li key={group}>
                    <p className="mb-1 mt-4 flex items-center gap-2 px-3 text-[10px] font-semibold uppercase tracking-widest text-navy-400">
                      {GROUP_STEP[group] !== undefined && (
                        <span className="font-mono text-gold-400">{GROUP_STEP[group]}</span>
                      )}
                      {group}
                    </p>
                    <ul className="flex flex-col gap-1">
                      {NAV.filter((item) => item.group === group).map((item) => (
                        <NavRow key={item.to} workspaceId={navWorkspaceId} item={item} />
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>

              {/* Guardrails + reference, pinned below the loop. */}
              <ul className="mt-auto flex flex-col gap-1 border-t border-navy-600 pt-3">
                {GOVERN_NAV.map((item) => (
                  <NavRow key={item.to} workspaceId={navWorkspaceId} item={item} />
                ))}
              </ul>
              <OrgSection />
            </>
          )}
        </nav>

        {/* Persistent guidance: follows the user on every screen until the loop
            is proven once, then disappears. Driven by real workspace state. */}
        {workspaceId !== undefined && !setupComplete && nextStep !== undefined && (
          <div className="mx-3 mb-3 rounded-lg border border-navy-600 bg-navy-800 p-3">
            <p className="text-xs font-semibold text-ink-inverse">Finish setup</p>
            <p className="mt-0.5 text-[11px] text-navy-300">
              {setupDone} of {setupSteps.length} done — {setupSteps.length - setupDone} to your
              first live post.
            </p>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-navy-600">
              <div
                className="h-full rounded-full bg-gold-400"
                style={{ width: `${String((setupDone / setupSteps.length) * 100)}%` }}
              />
            </div>
            <button
              type="button"
              onClick={() => navigate(`/w/${workspaceId}/${nextStep.to}`)}
              className="mt-2.5 w-full rounded-md bg-gold-400 py-1.5 text-xs font-semibold text-navy-900 transition-colors duration-fast hover:bg-gold-500"
            >
              {nextStep.label} →
            </button>
          </div>
        )}

        <div className="border-t border-navy-600 px-5 py-3">
          <p className="truncate text-xs text-navy-300">
            {navWorkspace?.name ?? 'No workspace selected'}
          </p>
          {/* Sign out lives in the header on desktop; the drawer carries it on
              mobile, where the header hides it for space. */}
          <button
            type="button"
            onClick={() => void signOut()}
            className="mt-3 flex w-full items-center justify-center rounded-md border border-navy-500 py-1.5 text-xs font-semibold text-navy-200 transition-colors duration-fast hover:bg-navy-600 hover:text-ink-inverse lg:hidden"
          >
            Sign out
          </button>
          <p className="mt-3 text-[10px] uppercase tracking-widest text-gold-400">
            Integrity · Trust · Value
          </p>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-edge bg-surface px-4 py-2.5 sm:gap-4 sm:px-6">
          {/* Hamburger — opens the off-canvas rail on mobile only. */}
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
            aria-expanded={navOpen}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-edge text-navy-700 transition-colors duration-fast hover:border-navy-400 hover:bg-surface-sunken lg:hidden"
          >
            <MenuIcon size={20} />
          </button>
          <Cluster gap="sm" className="hidden min-w-0 sm:flex">
            {workspaceId !== undefined && (
              <button
                type="button"
                onClick={() => navigate(`/w/${workspaceId}/dashboard`)}
                title="Home"
                aria-label="Home"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-edge text-ink-muted transition-colors duration-fast hover:border-navy-400 hover:text-navy-600"
              >
                <HomeIcon size={18} />
              </button>
            )}
            <Breadcrumb items={crumbs} className="min-w-0" />
          </Cluster>

          {/* Global command bar — find anything, or run an action (⌘K). */}
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-label="Find or publish"
            className="mx-auto flex h-9 w-full max-w-md items-center gap-2.5 rounded-lg border border-edge bg-surface-sunken px-3 text-sm text-ink-muted transition-colors duration-fast hover:border-navy-400"
          >
            <SearchIcon size={15} className="shrink-0" />
            <span className="truncate">Find or publish…</span>
            <kbd className="ml-auto hidden rounded border border-edge bg-surface px-1.5 py-0.5 font-mono text-[10px] text-ink-muted sm:inline-block">
              ⌘K
            </kbd>
          </button>

          <Cluster gap="sm">
            {workspaceId !== undefined && (
              <>
                <button
                  type="button"
                  onClick={() => setPaletteOpen(true)}
                  title="Switch workspace"
                  className="hidden items-center gap-1.5 rounded-md border border-edge px-2.5 py-1.5 text-xs font-medium text-navy-700 transition-colors duration-fast hover:border-navy-400 md:flex"
                >
                  <SwitchIcon size={14} />
                  <span className="max-w-[9rem] truncate">
                    {activeWorkspace?.name ?? 'Workspace'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/w/${workspaceId}/campaigns?launch=review-publish`)}
                  className="flex items-center gap-1.5 rounded-md bg-gold-400 px-3.5 py-1.5 text-sm font-semibold text-navy-900 transition-colors duration-fast hover:bg-gold-500"
                >
                  <span aria-hidden="true">✦</span> Publish
                </button>
                <button
                  type="button"
                  onClick={() => setPaletteOpen(true)}
                  title="Quick create"
                  aria-label="Quick create"
                  className="hidden h-8 w-8 items-center justify-center rounded-md border border-edge text-ink-muted transition-colors duration-fast hover:border-navy-400 hover:text-navy-600 sm:flex"
                >
                  <PlusIcon size={16} />
                </button>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void signOut()}
              className="hidden sm:inline-flex"
            >
              Sign out
            </Button>
          </Cluster>
        </header>
        <main className="flex-1">
          {/* Keyed on the path so navigating away from a broken screen resets
              the boundary rather than stranding the operator on the error. */}
          <ErrorBoundary key={pathname}>{children}</ErrorBoundary>
        </main>
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        commands={commands}
      />

      {toast !== null && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-5 right-5 z-50 rounded-lg border border-navy-600 bg-navy-800 px-4 py-2.5 text-sm text-ink-inverse shadow-raised"
        >
          {toast}
        </div>
      )}
    </div>
  );
}
